import { useEffect, useState } from 'react';
import { Alert, Button, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { Permission } from '@aid/shared';
import { EmptyState } from '../../components/EmptyState';
import { LoadingScreen } from '../../components/LoadingScreen';
import { useHasPermission } from '../../auth/useHasPermission';
import { useT } from '../../locales';
import { useRole, useUpdateRole } from './api/useRoles';
import {
  RoleForm,
  roleResponseToFormValues,
  ROLE_FORM_ID,
  type RoleFormValues,
} from './components/RoleForm';
import { RoleEditorHeader } from './components/RoleEditorHeader';
import { RoleUnsavedChangesModal } from './components/RoleUnsavedChangesModal';
import { summarizePermissionNames, type PermissionSummary } from './permission-metadata';
import { useRoleUnsavedChanges } from './useRoleUnsavedChanges';
import './roles.css';

const EMPTY_ROLE_FORM_VALUES: RoleFormValues = {
  name: '',
  description: '',
  isActive: true,
  permissionIds: [],
};

export function RoleEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const t = useT();
  const { data: role, isLoading, error, refetch } = useRole(id ?? '');
  const updateRole = useUpdateRole();
  const canRead = useHasPermission(Permission.ROLES_READ);
  const canUpdate = useHasPermission(Permission.ROLES_UPDATE);
  const [draft, setDraft] = useState<RoleFormValues>(EMPTY_ROLE_FORM_VALUES);
  const [permissionSummary, setPermissionSummary] = useState<PermissionSummary>({
    permissionCount: 0,
    moduleCount: 0,
  });
  const unsavedChanges = useRoleUnsavedChanges({
    onDiscard: () => navigate('/roles'),
  });

  useEffect(() => {
    if (!role) return;
    setDraft(roleResponseToFormValues(role));
    setPermissionSummary(
      summarizePermissionNames(role.permissions.map((permission) => permission.name)),
    );
  }, [role?.id]);

  if (!canRead) {
    return <EmptyState title={t('pages.roles.noPermission')} compact />;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error || !role) {
    return (
      <EmptyState
        title={t('pages.roles.notFound')}
        description={t('pages.roles.notFoundDescription')}
        action={<Button onClick={() => void refetch()}>{t('pages.roles.retryRole')}</Button>}
      />
    );
  }

  const readOnly = !canUpdate;

  const handleSubmit = async (values: RoleFormValues) => {
    if (readOnly) return;

    try {
      await updateRole.mutateAsync({ id: role.id, body: values });
      unsavedChanges.markClean();
      message.success(t('pages.roles.saveSuccess'));
      navigate('/roles');
    } catch {
      message.error(t('pages.roles.saveError'));
    }
  };

  const handleValuesChange = (values: RoleFormValues) => {
    setDraft(values);
  };

  return (
    <div className="role-editor-page">
      <RoleEditorHeader
        title={draft.name || role.name}
        description={draft.description}
        isActive={draft.isActive}
        selectedPermissionCount={permissionSummary.permissionCount}
        selectedModuleCount={permissionSummary.moduleCount}
        isDirty={unsavedChanges.isDirty}
        readOnly={readOnly}
        saving={updateRole.isPending}
        onBack={unsavedChanges.requestDiscard}
        onDiscard={unsavedChanges.requestDiscard}
        formId={ROLE_FORM_ID}
      />

      {readOnly && (
        <Alert
          type="warning"
          showIcon
          message={t('pages.roles.readOnlyWarning')}
          style={{ marginBottom: 24 }}
        />
      )}

      <RoleForm
        mode="edit"
        initialValues={role}
        onSubmit={handleSubmit}
        onCancel={unsavedChanges.requestDiscard}
        loading={updateRole.isPending}
        readOnly={readOnly}
        onDirtyChange={unsavedChanges.setIsDirty}
        onValuesChange={handleValuesChange}
        onPermissionSummaryChange={setPermissionSummary}
      />

      <RoleUnsavedChangesModal
        open={unsavedChanges.confirmOpen}
        onKeepEditing={unsavedChanges.handleKeepEditing}
        onDiscard={unsavedChanges.handleDiscard}
      />
    </div>
  );
}
